import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  type PaginationState,
  useReactTable,
} from '@tanstack/react-table'
import React, { useEffect, useMemo, useState } from 'react'

import { InputBlock } from 'slab'
import { useGetAdminMedicalPlanProjections } from 'utils/web_api'
import {
  type AdminMedicalPlanProjection,
  type GetAdminMedicalPlanProjectionsParams
} from 'utils/web_api.schemas'

import TanstackTable, { type PaginationMetadata } from './reusable/table/TanstackTable'

type MedicalPlanProjectionTableFilters = {
  carrier_name?: string
  state?: string
}

const columnHelper = createColumnHelper<AdminMedicalPlanProjection>()
const columns = [
  columnHelper.accessor('state', {
    header: 'State',
    cell: (info) => <div data-testid={`state-${info.row.id}`}>{info.getValue()}</div>,
    enableSorting: false,
  }),
  columnHelper.accessor('carrier_name', {
    header: 'Carrier Name',
    cell: (info) => {
      const value = info.getValue()
      if (value) {
        return <div data-testid={`carrier-${info.row.id}`}>{value}</div>
      } else {
        return <div data-testid={`carrier-${info.row.id}`} className='text-gray-400 italic'>state wide fallback</div>
      }
    },
    enableSorting: false,
  }),
  columnHelper.accessor('carrier_hios_id', {
    header: 'Carrier HIOS ID',
    cell: (info) => {
      const value = info.getValue()
      if (value) {
        return <div data-testid={`carrier-hios-${info.row.id}`}>{value}</div>
      } else {
        return <div data-testid={`carrier-hios-${info.row.id}`} className='text-gray-400 italic'>&mdash;</div>
      }
    },
    enableSorting: false,
  }),
  columnHelper.accessor('projected_rate_mult', {
    header: 'Rate Multiplier',
    cell: (info) => {
      const value = info.getValue()
      if (value || value === 0) {
        return <div data-testid={`rate-${info.row.id}`}>{(value * 100.0).toFixed(2)}%</div>
      } else {
        return <div data-testid={`rate-${info.row.id}`} className='text-gray-400 italic'>carrier excluded</div>
      }
    },
    enableSorting: false,
  }),
]

const MedicalPlanProjectionsTable: React.FC<{ year: number }> = ({ year }) => {
  const [filters, setFilters] = useState<MedicalPlanProjectionTableFilters>({})

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 1,
    pageSize: 20,
  })

  const [paginationMetadata, setPaginationMetadata] = useState<
    PaginationMetadata | undefined
  >()

  // Transform filters and pagination to match admin API parameters
  const adminApiParams = useMemo(() => {
    const params: GetAdminMedicalPlanProjectionsParams = {
      year,
      'page[number]': pagination.pageIndex,
      'page[size]': pagination.pageSize,
    }

    if (filters.carrier_name) {
      params.carrier_name = filters.carrier_name
    }

    if (filters.state) {
      params.state = filters.state
    }

    return params
  }, [filters, pagination, year])

  const {
    data: projectionsData,
    isLoading,
    isError,
  } = useGetAdminMedicalPlanProjections(adminApiParams)

  const table = useReactTable({
    data: projectionsData?.data || [],
    columns,
    manualPagination: true,
    manualSorting: false,
    enableSorting: false,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row) => row.id,
    state: {
      pagination,
    },
  })

  useEffect(() => {
    if (projectionsData?.pagination) {
      setPaginationMetadata(projectionsData.pagination)
    }
  }, [projectionsData?.pagination])

  const handleFilterChange = (field: keyof MedicalPlanProjectionTableFilters, value: string | number) => {
    setFilters((prev: MedicalPlanProjectionTableFilters) => ({
      ...prev,
      [field]: value || undefined,
    }))
    // Reset to first page when filters change
    setPagination((prev) => ({ ...prev, pageIndex: 1 }))
  }

  const uniqueStates = useMemo(() => {
    const states = [
      'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
      'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
      'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
      'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
      'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
    ]
    return states.sort()
  }, [])

  return (
    <div className='space-y-6'>
      <div className='bg-white shadow rounded-lg p-6'>
        <h2 className='text-lg font-medium text-gray-900 mb-4'>Filters</h2>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <InputBlock.Select
            label='State'
            value={filters.state || ''}
            onChange={(e) => handleFilterChange('state', e.target.value)}
            placeholder='All States'
          >
            {uniqueStates.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </InputBlock.Select>

          <InputBlock.Input
            label='Carrier Name'
            value={filters.carrier_name || ''}
            onChange={(e) => handleFilterChange('carrier_name', e.target.value)}
            placeholder='Enter carrier name...'
          />
        </div>
      </div>

      {/* Results Table */}
      <div>
        <TanstackTable
          table={table}
          isLoading={isLoading}
          isError={isError}
          noDataMessage='No medical plan projections found for the selected criteria.'
          paginationLabelSingular='projection'
          paginationLabelPlural='projections'
          paginationMetadata={paginationMetadata}
          onNextPageClick={() =>
            setPagination({
              ...pagination,
              pageIndex: paginationMetadata?.next_page || 1,
            })}
          onPreviousPageClick={() =>
            setPagination({
              ...pagination,
              pageIndex: paginationMetadata?.prev_page || 1,
            })}
        />
      </div>
    </div>
  )
}

export default MedicalPlanProjectionsTable
